/**
 * @file src/components/ConnectedAccount.tsx
 * @description Defines the ConnectedAccount component, which displays the details of the currently connected authentication provider, including the provider name and associated icon.
 * @module ConnectedAccount
 */

import { Icon, ListItem } from '@/components';
import Section from '@/components/atoms/Section/Section';
import { useAuth } from '@/components/providers/preauth/AuthProvider';
import { formatToSentenceCase } from '@/utils/formatUtils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface AuthProviderDetailsInterface {
  providerName: string;
  providerValue: string;
}

const ConnectedAccount: React.FC = () => {
  const { getAuthProviderDetails } = useAuth();
  const [signInDetails, setSignInDetails] =
    useState<AuthProviderDetailsInterface>({
      providerName: '',
      providerValue: '',
    });

  const fetchProviderName = useCallback(async () => {
    const providerDetails = await getAuthProviderDetails();
    setSignInDetails({
      providerName: providerDetails.providerName,
      providerValue: providerDetails.providerValue,
    });
  }, [getAuthProviderDetails]);

  useEffect(() => {
    fetchProviderName();
  }, [fetchProviderName]);

  const providerIcon = useMemo(() => {
    switch (signInDetails.providerName) {
      case 'google.com':
        return <Icon name="google" intent="primary" size={'lg'} />;
      case 'twitter.com':
        return <Icon name="x" intent="primary" size={'lg'} />;
      case 'facebook.com':
        return <Icon name="facebook" intent="primary" size={'lg'} />;
      case 'phone':
        return <Icon name="phone" intent="primary" size={'lg'} />;
      case 'password':
        return <Icon name="mail" intent="primary" size={'lg'} />;
      default:
        return null;
    }
  }, [signInDetails.providerName]);

  const providerName = useMemo(() => {
    switch (signInDetails.providerName) {
      case 'google.com':
        return 'Google';
      case 'twitter.com':
        return 'X';
      case 'facebook.com':
        return 'Facebook';
      case 'phone':
        return 'Phone';
      case 'password':
        return 'Email';
      default:
        return '';
    }
  }, [signInDetails.providerName]);

  return (
    <Section
      headerTitle="Connected account"
      body={
        <ListItem
          leftIcon={providerIcon}
          mainText={formatToSentenceCase(providerName)}
          subText={signInDetails.providerValue}
        ></ListItem>
      }
    />
  );
};

export default ConnectedAccount;
